import { Component } from '@angular/core';

import { DOCS_ENDPOINTS } from '../../pages/docs/data/docs-api-data';
import { DOCS_BASE_URL } from '../../pages/docs/utils/docs-code';

/**
 * The branded mosaic beside the auth form, drawn from the Figma "left side"
 * group (file ETDAJntgDBU2VEunI0A5aP, node 221:6787).
 *
 * It's a fixed 960x1048 design box sliced to cover the panel, which crops it
 * exactly the way `object-fit: cover` cropped the PNG it replaces. Every
 * number below is in that design box's units and comes straight out of
 * Figma — nothing here is eyeballed, including the text baselines, which
 * Figma computes as `box top + (lineHeight - (ascent + descent)) / 2 +
 * ascent`.
 *
 * Grid: 320x400 tiles, radius 12, 24px gutter, origin (-196, -312) so the
 * mosaic bleeds off all four edges.
 *
 * What the cards and the ambient code *say* is not Figma's, though. The
 * mockup showed an invented API — api.mila.dev, POST /api/v2/search,
 * GET /api/v2/answer, an "@mila/sdk" package, x-wallet-* response headers —
 * none of which exists in this product. Every path, header, request body,
 * response field and sample value below is now the real documented
 * contract, and it is read out of the docs' own source of truth
 * (pages/docs/data/docs-api-data.ts, pages/docs/utils/docs-code.ts) rather
 * than re-typed here, so the panel can't drift away from /docs: a reader
 * who lands on /login and then on /docs/api-reference sees one API, not
 * two.
 *
 * The search card's layout moved with its content. A real /rag response is
 * a couple of rich snippets, not five one-line hits, so each row now shows
 * what the endpoint actually returns — article title, the passage itself,
 * its relevance score and the domain — instead of five invented headlines.
 */

/** One 320x400 tile, positioned in design-box coordinates. */
interface Tile {
  x: number;
  y: number;
}

/** A column of the ambient code sheet, clipped to the white tiles. */
interface CodeColumn {
  x: number;
  /** Baseline of the first line. */
  y: number;
  opacity: number;
  lines: string[];
}

/** A row of the "POST /api/rag" card: one snippet out of the endpoint's own
 *  documented sample response. */
interface SnippetRow {
  rank: string;
  /** SVG has no line breaking, so every string is wrapped here. */
  title: string[];
  excerpt: string[];
  /** relevance_score, as shown next to the bar. */
  score: string;
  /** Width of the relevance bar's filled part, in design px. */
  bar: number;
  domain: string;
  /** Baseline of the row's first line. */
  y: number;
}

/** A pill in either card's footer. Widths are Figma's, not computed. */
interface Chip {
  label: string;
  x: number;
  w: number;
}

/** One syntax-coloured run inside the response body. */
interface JsonSpan {
  t: string;
  c?: 'key' | 'str' | 'num';
}

export const TILE_W = 320;
export const TILE_H = 400;

/** Lines per ambient column — enough to run past both edges of the box. */
const COLUMN_LINES = 88;

/** Full relevance bar, in design px — a row's fill is score × this. */
const BAR_W = 34;

/** The documented endpoints, by id. Throws rather than rendering a blank
 *  card if one is ever renamed in docs-api-data.ts. */
function endpoint(id: string) {
  const found = DOCS_ENDPOINTS.find((e) => e.id === id);
  if (!found) throw new Error(`auth-panel: no documented endpoint "${id}"`);
  return found;
}

const RAG = endpoint('rag');
const ANSWER = endpoint('answer');

/** The shape of RAG's own sample snippets — `sampleResponse.data` is typed
 *  as an open record over there (it holds a different shape per endpoint),
 *  so the fields this card reads are named once here. */
interface SampleSnippet {
  snippet: string;
  relevance_score: number;
  article_title: string;
  domain_name: string;
}

interface SampleAnswerMeta {
  mode: string;
  output_type: string;
  source_count: number;
}

const RAG_SNIPPETS = RAG.sampleResponse.data['snippets'] as SampleSnippet[];
const ANSWER_TEXT = ANSWER.sampleResponse.data['answer'] as string;
const ANSWER_META = ANSWER.sampleResponse.data['meta'] as SampleAnswerMeta;
const ANSWER_SOURCE = (ANSWER.sampleResponse.data['sources'] as { domain_name: string }[])[0];

/**
 * Greedy wrap to a character budget, cut to `max` lines with a trailing
 * ellipsis when the text runs past them. Character counting rather than
 * measurement: these strings come from the docs sample payloads, the fonts
 * are known, and the budgets below are set well inside each card's inner
 * width, so a proportional font's variance can't reach the edge. A word
 * longer than the budget is left long rather than broken.
 */
function wrap(text: string, width: number, max: number): string[] {
  const lines: string[] = [];
  let line = '';

  for (const word of text.split(/\s+/)) {
    const next = line ? `${line} ${word}` : word;
    if (next.length <= width || !line) {
      line = next;
      continue;
    }
    lines.push(line);
    line = word;
    if (lines.length === max) break;
  }

  if (lines.length < max && line) lines.push(line);
  if (lines.length === max && lines.join(' ').length < text.length) {
    lines[max - 1] = `${lines[max - 1].replace(/[,.;:]$/, '')}…`;
  }

  return lines;
}

/* ── Ambient code ──────────────────────────────────────────────────
   Texture, not content: kept under 46 characters so columns pitched
   340px apart never collide. Every block is a real call or a real
   response — the request/response envelope docsCallExamples() renders on
   the docs pages, the { success, messages, data } body documented on
   Making a call, and the error envelope from the Errors page. */

const RAG_PYTHON = [
  'import requests',
  '',
  'response = requests.post(',
  `    "${DOCS_BASE_URL}${RAG.path}",`,
  '    headers={',
  '        "Authorization": "Bearer cks_live_…",',
  '    },',
  '    json={',
  '        "request": {',
  '            "question": "What are the key',
  '                announcements of Skanska',
  '                for last 6 months?",',
  '        },',
  '        "response": {',
  '            "comprehensiveness": 120',
  '        },',
  '    },',
  ')',
  '',
  'data = response.json()',
];

const SEARCH_CURL = [
  'curl -X POST \\',
  `  "${DOCS_BASE_URL}${endpoint('search').path}" \\`,
  '  -H "Authorization: Bearer cks_live_…" \\',
  '  -H "Content-Type: application/json" \\',
  "  -d '{",
  '    "request": {',
  '      "query": "Financial results 2025',
  '        Airbus OR defense contracts NATO",',
  '      "sort_by": "relevance",',
  '      "max_results": 100',
  '    },',
  '    "response": {"include_title": true}',
  "  }'",
];

const ANSWER_JS = [
  'const res = await fetch(',
  `  "${DOCS_BASE_URL}${ANSWER.path}",`,
  '  {',
  '    method: "POST",',
  '    headers: {',
  '      Authorization: "Bearer cks_live_…",',
  '      "Content-Type": "application/json",',
  '    },',
  '    body: JSON.stringify({',
  '      request: {',
  '        question: "What are Apple\'s key',
  '          product announcements from the',
  '          last 6 months?",',
  '        include_languages: ["en", "fr"],',
  '      },',
  '      response: { mode: "reasoning" },',
  '    }),',
  '  },',
  ');',
  '',
  'const data = await res.json();',
];

const RAG_RESPONSE = [
  '{',
  '  "success": true,',
  '  "messages": ["Passages retrieved',
  '    successfully"],',
  '  "data": {',
  '    "question": "What are the key',
  '      announcements of Skanska for last',
  '      6 months?",',
  '    "snippets": [',
  '      {',
  '        "snippet": "Skanska has been',
  '          awarded a $420 million',
  '          contract to build a new',
  '          highway interchange…",',
  '        "url": "https://www.skanska.com/',
  '          newsroom/press-releases/2026/',
  '          skanska-wins-contract",',
  '        "published_at":',
  '          "2026-05-12T09:00:00Z",',
  '        "relevance_score": 0.9123,',
  '        "article_title": "Skanska wins',
  '          major infrastructure contract",',
  '        "domain_name": "skanska.com"',
  '      }',
  '    ],',
  '    "meta": {"count": 2,',
  '      "most_frequent_answer": null}',
  '  }',
  '}',
];

const ANSWER_RESPONSE = [
  '{',
  '  "success": true,',
  '  "messages": ["Answer returned',
  '    successfully"],',
  '  "data": {',
  '    "answer": "Apple announced the',
  '      iPhone 17 lineup with a redesigned',
  '      camera system, expanded its',
  '      services bundle with a new',
  '      AI-powered assistant tier…",',
  '    "sources": [',
  '      {',
  '        "title": "Apple announces',
  '          iPhone 17 lineup",',
  '        "published_at":',
  '          "2026-06-09T17:00:00Z",',
  '        "domain_name": "apple.com"',
  '      }',
  '    ],',
  '    "meta": {"mode": "direct",',
  '      "output_type": "unstructured",',
  '      "source_count": 71}',
  '  }',
  '}',
];

const HTTP_AUTH = [
  `POST ${DOCS_BASE_URL}${RAG.path}`,
  'Authorization: Bearer cks_live_…',
  'Content-Type: application/json',
];

const RATE_LIMIT_ERROR = [
  'HTTP/1.1 429 Too Many Requests',
  'Content-Type: application/json',
  '',
  '{',
  '  "error": {',
  '    "code": "rate_limit_exceeded",',
  '    "message": "Over 60 requests in a',
  '      minute for this key.",',
  '    "details": {"retry_after": …}',
  '  }',
  '}',
];

/** Cycles `blocks` (blank line between each) until the column is full. */
function column(x: number, y: number, opacity: number, blocks: string[][]): CodeColumn {
  const lines: string[] = [];

  for (let i = 0; lines.length < COLUMN_LINES; i++) {
    lines.push(...blocks[i % blocks.length], '');
  }

  return { x, y, opacity, lines: lines.slice(0, COLUMN_LINES) };
}

/** Baseline of the first row on the /rag card, and the pitch between rows.
 *  A row is title (2 lines) + passage (3) + the score/domain footer. */
const ROW_Y = 218;
const ROW_PITCH = 130;

@Component({
  selector: 'app-auth-panel',
  standalone: false,
  templateUrl: './auth-panel.html',
})
export class AuthPanel {
  readonly tileW = TILE_W;
  readonly tileH = TILE_H;

  /** White tiles. The ambient code sheet is clipped to exactly these. */
  readonly codeTiles: Tile[] = [
    { x: -196, y: -312 }, { x: 148, y: -312 }, { x: 492, y: -312 }, { x: 836, y: -312 },
    { x: -196, y: 112 },  { x: 148, y: 112 },  { x: 492, y: 112 },  { x: 836, y: 112 },
    { x: -196, y: 536 },  { x: 148, y: 536 },  { x: 492, y: 536 },  { x: 836, y: 536 },
    { x: -196, y: 960 },  { x: 148, y: 960 },  { x: 492, y: 960 },  { x: 836, y: 960 },
  ];

  /**
   * The blue shape is one boolean union, not four tiles — the 92px fillets
   * are what join its corners. Exported from Figma; drawn in the union's own
   * space, which sits 148px right of the design box's origin.
   */
  readonly brandPath =
    'M356 -312C349.373 -312 344 -306.627 344 -300V42.0587C344 60.6082 336.631 78.3981 323.515 ' +
    '91.5146C310.398 104.631 292.608 112 274.059 112H12C5.37256 112 0 117.373 0 124V500C0 506.627 ' +
    '5.37256 512 12 512H274.017C292.578 512 310.378 519.373 323.502 532.498C336.627 545.622 344 ' +
    '563.422 344 581.983V890.059C344 908.608 336.631 926.398 323.515 939.515C310.398 952.631 ' +
    '292.608 960 274.059 960H12C5.37256 960 0 965.373 0 972V1348C0 1354.63 5.37256 1360 12 ' +
    '1360H308C314.627 1360 320 1354.63 320 1348V1005.94C320 987.392 327.369 969.602 340.485 ' +
    '956.485C353.602 943.369 371.392 936 389.941 936H652C658.627 936 664 930.627 664 924V548C664 ' +
    '541.373 658.627 536 652 536H389.9C371.361 536 353.582 528.636 340.473 515.527C327.364 ' +
    '502.418 320 484.639 320 466.1V157.941C320 139.392 327.369 121.602 340.485 108.485C353.602 ' +
    '95.3688 371.392 88 389.941 88H652C658.627 88 664 82.6274 664 76V-300C664 -306.627 658.627 ' +
    '-312 652 -312H356Z';

  /**
   * -mila. wordmark, reverse variant: white letters, Signal Orange terminal
   * dot. Brand Guidelines v1 (node 2168:706), scaled from its 528x160.442
   * artboard to 240 wide and re-centred in the blue tile.
   */
  readonly logoPaths = [
    { id: 'dash', d: 'M0 83.8674H72.9282V108.663H0V83.8674Z', dot: false, evenodd: false },
    { id: 'm', d: 'M87.5138 157.525V94.8066C87.5138 67.0939 109.392 45.9447 137.105 45.9447C151.691 45.9447 164.818 51.779 173.569 61.2596C182.32 51.779 195.447 45.9447 211.492 45.9447C237.746 45.9447 256.707 65.6353 256.707 93.348V157.525H230.453V93.348C230.453 80.2209 222.431 71.4696 210.033 71.4696C196.906 71.4696 185.238 80.2209 185.238 93.348V157.525H158.983V93.348C158.983 80.2209 150.232 71.4696 137.105 71.4696C123.978 71.4696 113.768 80.2209 113.768 94.8066V131.271C113.768 145.856 102.099 157.525 87.5138 157.525Z', dot: false, evenodd: false },
    { id: 'i-stem', d: 'M272.022 157.525V52.5083C272.022 50.3205 273.481 48.8619 275.669 48.8619H291.713C295.359 48.8619 298.276 51.7791 298.276 55.4255V157.525H272.022Z', dot: false, evenodd: false },
    { id: 'i-dot', d: 'M301.923 16.7735C301.923 26.2541 294.63 33.547 285.149 33.547C275.669 33.547 268.376 26.2541 268.376 16.7735C268.376 7.29282 275.669 0 285.149 0C294.63 0 301.923 7.29282 301.923 16.7735Z', dot: false, evenodd: false },
    { id: 'l', d: 'M315.05 2.91711H329.635C336.199 2.91711 341.304 8.02209 341.304 14.5856V157.525H315.05V2.91711Z', dot: false, evenodd: false },
    { id: 'a', d: 'M471.845 157.525H446.32V138.563C436.84 151.691 423.713 158.983 405.481 158.983C375.58 158.983 353.702 134.188 353.702 103.558C353.702 70.7403 379.227 45.9447 413.503 45.9447C447.05 45.9447 471.845 69.2817 471.845 102.099V157.525ZM445.591 102.829C445.591 85.3259 431.735 71.4696 413.503 71.4696C395.271 71.4696 380.685 85.3259 380.685 102.829C380.685 121.061 394.541 134.188 412.773 134.188C431.006 134.188 445.591 120.331 445.591 102.829Z', dot: false, evenodd: true },
    { id: 'terminal-dot', d: 'M508.309 121.79C520.707 121.79 527.271 128.354 527.271 140.022V141.481C527.271 153.149 520.707 158.983 508.309 158.983C495.912 158.983 489.348 153.149 489.348 141.481V140.022C489.348 128.354 495.912 121.79 508.309 121.79Z', dot: true, evenodd: false },
  ];

  readonly codeColumns: CodeColumn[] = [
    column(-180, -317.5, 0.95, [RAG_PYTHON, RAG_RESPONSE, HTTP_AUTH]),
    column(160, -329.5, 0.65, [SEARCH_CURL, ANSWER_JS, ANSWER_RESPONSE]),
    column(500, -309.5, 0.95, [RAG_RESPONSE, RAG_PYTHON, SEARCH_CURL]),
    column(840, -325.5, 0.65, [ANSWER_JS, SEARCH_CURL, RATE_LIMIT_ERROR]),
  ];

  /* ── Card: POST /api/rag ──────────────────────────────────────── */

  readonly rag = RAG;
  readonly answer = ANSWER;
  readonly answerMeta = ANSWER_META;

  /** The endpoint's own documented sample snippets, wrapped to the card.
   *  Ranked in the order the API returns them (best match first). */
  readonly snippetRows: SnippetRow[] = RAG_SNIPPETS.map((s, i) => ({
    rank: String(i + 1).padStart(2, '0'),
    title: wrap(s.article_title, 26, 2),
    excerpt: wrap(s.snippet, 50, 3),
    score: s.relevance_score.toFixed(2),
    bar: Math.round(BAR_W * s.relevance_score),
    domain: s.domain_name,
    y: ROW_Y + i * ROW_PITCH,
  }));

  /** What the /rag example request above them actually asked for: the whole
   *  knowledge base, English and French, at 1 credit like every endpoint. */
  readonly ragChips: Chip[] = [
    { label: 'knowledge base', x: 512, w: 85 },
    { label: 'en · fr', x: 603, w: 45 },
    { label: '1 credit', x: 654, w: 50 },
  ];

  /* ── Card: POST /api/answer ───────────────────────────────────── */

  /** The documented sample response, abridged to the card: the same
   *  { success, data } envelope, the model's own answer text, one source,
   *  and the meta the real call comes back with. */
  readonly answerJson: JsonSpan[][] = [
    [{ t: '{' }],
    [{ t: '  ' }, { t: '"success"', c: 'key' }, { t: ': ' }, { t: 'true', c: 'num' }, { t: ',' }],
    [{ t: '  ' }, { t: '"data"', c: 'key' }, { t: ': {' }],
    ...wrap(ANSWER_TEXT, 28, 3).map((line, i, all) => {
      const last = i === all.length - 1;
      const text = `${i ? '' : '"'}${line}${last ? '"' : ''}`;

      return i === 0
        ? [{ t: '    ' }, { t: '"answer"', c: 'key' as const }, { t: ': ' }, { t: text, c: 'str' as const }]
        : [{ t: '      ' }, { t: text, c: 'str' as const }, ...(last ? [{ t: ',' }] : [])];
    }),
    [{ t: '    ' }, { t: '"sources"', c: 'key' }, { t: ': [' }],
    [{ t: '      {' }, { t: '"domain_name"', c: 'key' }, { t: ': ' }, { t: `"${ANSWER_SOURCE.domain_name}"`, c: 'str' }, { t: ', …}, …' }],
    [{ t: '    ],' }],
    [{ t: '    ' }, { t: '"meta"', c: 'key' }, { t: ': {' }, { t: '"source_count"', c: 'key' }, { t: ': ' }, { t: String(ANSWER_META.source_count), c: 'num' }, { t: '}' }],
    [{ t: '  }' }],
    [{ t: '}' }],
  ];

  readonly answerChips: Chip[] = [
    { label: ANSWER_META.output_type, x: 168, w: 72 },
    { label: `${ANSWER_META.source_count} sources`, x: 246, w: 58 },
    { label: 'en · fr', x: 310, w: 45 },
  ];

  trackByIndex(index: number): number {
    return index;
  }
}
